import { zodResolver } from '@hookform/resolvers/zod';
import { CircularProgress } from '@mui/material';
import { CContainedButton } from 'components/atoms';
import { COutlinedInput, InfoCard } from 'components/molecules';
import { CardType } from 'components/pages/checkout/utils/paymentValidations';
import { useAppContext } from 'hooks/context';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useStoreCardMutation } from 'services/payment';
import constants from 'utils/constants';
import { z } from 'zod';

interface AddCreditCardProps {
  onSuccess?: () => void;
}

export default function AddCreditCard({ onSuccess }: AddCreditCardProps) {
  const { dialog } = useAppContext();
  const { t } = useTranslation(['common', 'translation']);

  const numberOfCCInputRef = useRef<HTMLInputElement>(null);
  const [maxLengthOfCC, setMaxLengthOfCC] = useState<number>();
  const [cursor, setCursor] = useState<number | null>(null);
  const [cardType, setCardType] = useState<CardType | null>(null);
  const [paymentResponse, setPaymentResponse] = useState<boolean | undefined>();

  const [addStoreCard, { isLoading: isAddingStoreCard }] = useStoreCardMutation();

  function formatCardNumber(cardNumber: string) {
    // remove all non digit characters
    const value = cardNumber.replace(/\D/g, '');
    let formattedValue = '';
    let maxLength;
    // american express, 15 digits
    if (/^3[47]\d{0,13}$/.test(value)) {
      formattedValue = value.replace(/(\d{4})/, '$1 ').replace(/(\d{4}) (\d{6})/, '$1 $2 ');
      maxLength = 17;
    } else if (/^\d{0,16}$/.test(value)) {
      // regular cc number, 16 digits
      formattedValue = value
        .replace(/(\d{4})/, '$1 ')
        .replace(/(\d{4}) (\d{4})/, '$1 $2 ')
        .replace(/(\d{4}) (\d{4}) (\d{4})/, '$1 $2 $3 ');
      maxLength = 19;
    }

    setMaxLengthOfCC(maxLength);
    return formattedValue;
  }

  function validateCard(cardNumber: string) {
    const acceptedCreditCards = {
      visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
      mastercard: /^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$/,
      amex: /^3[47][0-9]{13}$/,
      troy: /^(?:9792|65\d{2}|36|2205)\d{12}$/,
    };
    // remove all non digit characters
    const value = cardNumber.replace(/\D/g, '');
    let sum = 0;
    let shouldDouble = false;
    // loop through values starting at the rightmost side
    for (let i = value.length - 1; i >= 0; i--) {
      let digit = parseInt(value.charAt(i));

      if (shouldDouble) {
        if ((digit *= 2) > 9) digit -= 9;
      }

      sum += digit;
      shouldDouble = !shouldDouble;
    }

    const valid = sum % 10 === 0;
    let accepted = false;
    setCardType(null);

    // loop through the keys (visa, mastercard, amex, etc.)
    if (valid) {
      (Object.keys(acceptedCreditCards) as (keyof typeof acceptedCreditCards)[]).forEach(
        (key, index) => {
          const regex = acceptedCreditCards[key];
          if (regex.test(value)) {
            accepted = true;
            setCardType(index);
          }
        }
      );
    }

    return accepted;
  }

  function formatExpDate(expDate: string) {
    const value = expDate.replace(/\D/g, '');
    let formattedValue = '';
    if (/^\d{0,4}$/.test(value)) {
      formattedValue = value.replace(/(\d{2})(\d{2})/, '$1/$2');
    }

    return formattedValue;
  }

  const schema = z.object({
    holderNameSurname: z.string().min(2, { message: t('translation:schema.required') }),
    cardNo: z.string({ required_error: t('translation:schema.required') }).refine(
      (value) => {
        if (!value || value.trim() === '') return true;
        return validateCard(value);
      },
      { message: t('translation:schema.invalid_cc') }
    ),
    expDate: z.string().min(1, { message: t('translation:schema.required') }),
    securityCode: z.string().min(1, { message: t('translation:schema.required') }),
    cardAlias: z.string().min(1, { message: t('translation:schema.required') }),
  });

  const {
    control,
    setValue,
    watch,
    trigger,
    getValues,
    handleSubmit,
    formState: { touchedFields, errors, isValid },
  } = useForm({
    defaultValues: {
      holderNameSurname: '',
      cardNo: '',
      expDate: '',
      securityCode: '',
      cardAlias: '',
    },
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const onSubmit = (data: any) => {
    console.log(data);
    const payload = {
      cardAlias: data.cardAlias,
      cardNumber: data.cardNo.replace(/\s/g, ''),
      cardOwnerName: data.holderNameSurname,
      year: data.expDate.split('/')[1],
      month: data.expDate.split('/')[0],
      cvc: data.securityCode,
    };
    addStoreCard({ data: payload })
      .unwrap()
      .then((res) => {
        setPaymentResponse(true);
        if (res.isSuccess) {
          setPaymentResponse(true);
          return dialog.showDialog({
            onClose: () => setPaymentResponse(),
            children: (
              <iframe
                className="h-[50vh] min-h-[450px] w-full self-center"
                title="payment"
                src={`${constants.siteUrl}/payment/PaymentDetail?PaymentCreateId=${res?.data?.paymentCreateId}&IframeSecretKey=${res?.data?.iframeSecretKey}`}
              />
            ),
          });
        }
      });
  };

  const paymentHandler = (event: any) => {
    const { payload, platform } = JSON.parse(event.data || '{}');

    if (platform === 'kochealthcareodeme') {
      // if payment is succes, payload doesnt return -1
      // if payment is succes, navigate to homepage. if not, navigate to basket page
      if (!payload.startsWith('-1')) {
        dialog.closeDialog();
        onSuccess?.(); // if parent component passed onSuccess prop, call it
      } else {
        dialog.closeDialog();
        setPaymentResponse();
      }
    }
  };

  useEffect(() => {
    paymentResponse && window.addEventListener('message', paymentHandler);

    return () => window.removeEventListener('message', paymentHandler);
  }, [paymentResponse]);

  return (
    <div className="flex flex-col gap-5">
      <InfoCard type="info" description={t('translation:account.add-credit-card-process-info')} />
      <form onSubmit={handleSubmit(onSubmit)} className="">
        <div className="grid grid-cols-1 gap-x-3 gap-y-5  md:grid-cols-2">
          <div className="col-span-2">
            <Controller
              control={control}
              name="holderNameSurname"
              render={({ field }) => (
                <COutlinedInput
                  {...field}
                  required
                  id="holderNameSurname"
                  label={t('translation:form.cardholder-name-surname')}
                  error={!!errors.holderNameSurname}
                  helperText={errors.holderNameSurname?.message}
                  className="w-full"
                />
              )}
            />
          </div>

          <div className="col-span-2">
            <Controller
              control={control}
              name="cardNo"
              render={({ field, fieldState: { isTouched } }) => (
                <COutlinedInput
                  {...field}
                  required
                  id="cardNo"
                  inputRef={numberOfCCInputRef}
                  label={t('translation:form.card-no')}
                  placeholder={t('translation:form.card-no-placeholder')}
                  className="w-full"
                  error={isTouched ? !!errors.cardNo : false}
                  helperText={isTouched ? errors.cardNo?.message : undefined}
                  // eslint-disable-next-line tailwindcss/no-custom-classname
                  inputProps={{
                    inputMode: 'numeric',
                    maxLength: maxLengthOfCC,
                  }}
                  onChange={(e) => {
                    field.onChange(e);

                    let cursor = e.target.selectionStart;
                    const lastValue = e.target.value;
                    const formattedValue = formatCardNumber(lastValue);
                    setValue('cardNo', formattedValue);

                    if (cursor === lastValue.length) {
                      cursor = formattedValue.length;
                      // decrement cursor when backspacing
                      // i.e. "4444 |" => backspace => "4444|"
                      if (
                        e.target.getAttribute('data-lastvalue') &&
                        e.target.getAttribute('data-lastvalue')?.charAt(cursor - 1) === ' '
                      ) {
                        cursor--;
                      }
                    }

                    if (lastValue !== formattedValue) {
                      // increment cursor when inserting character before a space
                      // i.e. "1234| 6" => "5" typed => "1234 5|6"
                      if (
                        cursor &&
                        lastValue.charAt(cursor) === ' ' &&
                        formattedValue.charAt(cursor - 1) === ' '
                      ) {
                        cursor++;
                      }
                    }

                    // set cursor position
                    setCursor(cursor);
                    // store last value
                    e.target.setAttribute('data-lastvalue', formattedValue);
                  }}
                />
              )}
            />
          </div>

          <Controller
            control={control}
            name="expDate"
            render={({ field }) => (
              <COutlinedInput
                {...field}
                required
                id="expDate"
                className="flex-1"
                label={t('translation:form.exp-date')}
                placeholder={t('translation:form.exp-date-placeholder')}
                error={!!errors.expDate}
                helperText={errors.expDate?.message}
                onChange={(e) => {
                  field.onChange(e);
                  const lastValue = e.target.value;
                  const formattedValue = formatExpDate(lastValue);
                  setValue('expDate', formattedValue);
                }}
              />
            )}
          />

          <Controller
            control={control}
            name="securityCode"
            render={({ field }) => (
              <COutlinedInput
                {...field}
                required
                id="securityCode"
                className="flex-1"
                label={t('translation:form.security-code')}
                error={!!errors.securityCode}
                helperText={errors.securityCode?.message}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: cardType === CardType.AMEX ? 4 : 3,
                }}
              />
            )}
          />
          <div className="col-span-2">
            <Controller
              control={control}
              name="cardAlias"
              render={({ field }) => (
                <COutlinedInput
                  {...field}
                  required
                  id="cardAlias"
                  label={t('translation:form.card-alias')}
                  error={!!errors.cardAlias}
                  helperText={errors.cardAlias?.message}
                  className="w-full"
                />
              )}
            />
          </div>

          <div className="col-span-full">
            <CContainedButton
              type="submit"
              className="w-full"
              color="primary"
              disabled={!isValid || isAddingStoreCard}
            >
              {isAddingStoreCard ? <CircularProgress size={24} /> : t('translation:form.save')}
            </CContainedButton>
          </div>
        </div>
      </form>
    </div>
  );
}

